/**
 * Horoscope page lifecycle events.
 *
 * The page-close event can be triggered from two places:
 *   1. The browser/WebView going away — `pagehide` / visibility→hidden.
 *   2. A CTA that redirects into the native app (which closes the WebView) — it
 *      fires close explicitly *before* the redirect so the event isn't lost.
 *
 * Both must resolve to a SINGLE close event, so this module guards it with a
 * once-flag. `firePageClose()` returns the send promise so the CTA path can
 * await it before navigating.
 */
import { AppEvent, flushEvents, logEvents } from '../lib/analytics'
import { EventAction, EventSection } from '../lib/eventConstants'

let pageCloseFired = false

/** Re-arm on page open (handles SPA re-mounts without a full reload). */
export function resetPageLifecycle(): void {
  pageCloseFired = false
}

/**
 * Claim the page-close event for the caller to BATCH into its own request
 * (e.g. a CTA that sends click + close together). Returns the event the first
 * time it's called, then null — so close is still counted at most once across
 * this path and the `pagehide`/visibility path.
 */
export function takePageCloseEvent(): AppEvent | null {
  if (pageCloseFired) return null
  pageCloseFired = true
  return { section: EventSection.HOROSCOPE_PAGE, action: EventAction.CLOSE }
}

/** Fire the page-close event standalone, at most once; returns the send promise. */
export function firePageClose(): Promise<void> {
  const event = takePageCloseEvent()
  // keepalive: fires as the page is being torn down (pagehide/visibility).
  return event ? logEvents([event], { keepalive: true }) : Promise.resolve()
}

/**
 * Register the native back-press hook on `window`. The native app calls
 * `window.onAskkWebViewClose()` just before destroying the WebView; we fire +
 * flush the close event (capped) and return the promise so native can await it.
 *
 * This is what makes back-button close reliable: a `pagehide` keepalive POST can
 * be cut off when native kills the WebView process, but if native calls this
 * first (and waits for it), the close event is flushed on a still-alive page —
 * exactly like the CTA-redirect path. Returns a cleanup function.
 */
export function installCloseBridge(): () => void {
  // Always resolve — native may await this, so never hand back a rejected promise.
  window.onAskkWebViewClose = () =>
    flushEvents([firePageClose()]).catch(() => undefined)
  return () => {
    delete window.onAskkWebViewClose
  }
}
